/**
 * GRIPS-INTERACTION PR-1 (§8.2 / §5.11) — click-arm interaction tunables +
 * grips-local labels. PR-2 (this file, mode-cycle addendum) adds the
 * Enter/Space edit-mode cycle constants.
 *
 * AutoCAD's hot-grip stretch has TWO ways to specify the destination:
 *   1. press-drag — hold the pointer on a grip, drag, release (already shipped).
 *   2. CLICK-ARM  — a single click on a grip warms it hot + enters the modal
 *      "** STRETCH **" phase; the NEXT click anywhere specifies the stretch
 *      point and commits (this slice).
 *
 * These constants live in a grips-local module (NOT `i18n/messages.ts`)
 * for the interaction TUNABLES (thresholds, cursor, indicator styling) —
 * those are not user-facing text. The RU/EN command-line echo strings
 * ("** STRETCH **" / "** MOVE **" / …) DO live in `i18n/messages.ts` under
 * the `grip.mode.*` keys (PR-2) — see `GRIP_MODE_LABELS` below for the
 * canonical AutoCAD token each mode maps to. `GRIP_ARMED_PROMPT_KEYS` below
 * (N4 GRIPS-ARMED-STRETCH-I18N, origin/main re-sync) reuses those SAME
 * `grip.mode.*` keys verbatim for its own banner line instead of minting a
 * parallel `prompt.grip.armed.*.mode` duplicate — one domain truth for "what
 * does the mode banner say", shared by both `GripArmedPrompt`'s two-line
 * command-line box and PR-2's `useGripModeEcho` single-line status strip.
 */

import type { MessageKey } from "../../i18n";

/**
 * Squared-distance-free pixel threshold a press must EXCEED (in either axis'
 * combined magnitude) between pointerdown and pointerup to count as a DRAG.
 * A release within the threshold is a CLICK → it arms the grip instead of
 * committing a zero-length stretch. Mirrors AutoCAD's PICKBOX-scale click
 * tolerance (a few device pixels); kept small so a deliberate nudge still
 * reads as a drag.
 */
export const GRIP_CLICK_ARM_DRAG_THRESHOLD_PX = 4;

/**
 * Modal "specify stretch point" cursor shown while a grip is click-armed —
 * the AutoCAD point-pick crosshair. Applied to the stage shell (and the armed
 * glyph itself) for the duration of the armed phase, restored on commit/abort.
 */
export const GRIP_ARMED_CURSOR = "crosshair";

/**
 * Armed-grip indicator ring — a detached square outline around the warmed grip
 * glyph so the click-armed grip reads distinctly from a merely-hot (Shift-warmed)
 * grip. Colour is the hot/armed accent; the ring is drawn as a CSS `outline`
 * with `outlineOffset` so it never resizes the glyph hit-box.
 */
export const GRIP_ARMED_INDICATOR_COLOR = "#ff2222";
export const GRIP_ARMED_INDICATOR_STROKE_PX = 1;
export const GRIP_ARMED_INDICATOR_PAD_PX = 2;

/**
 * Command-line mode label for the armed hot-grip edit. STRETCH is the
 * default hot-grip mode; pressing Enter or Spacebar while a grip is armed
 * cycles Stretch → Move → Rotate → Scale → Mirror → (wraps to Stretch) —
 * source-verified against Autodesk's own docs:
 *
 *   https://help.autodesk.com/cloudhelp/2023/ENU/AutoCAD-Core/files/GUID-484774B4-8F67-4E19-8647-83A7D314782B.htm
 *   "Press Enter or Spacebar to cycle to the move, rotate, scale, or mirror
 *   grip modes, or right-click the selected grip to view a shortcut menu
 *   with all available grip modes."
 *
 * CORRECTION (verified against the above): an earlier draft of this file and
 * the masterplan both mis-attributed the cycle to Ctrl. Ctrl does NOT drive
 * this cycle — it is reserved for a genuinely different AutoCAD feature (a
 * multifunctional grip's own per-grip-type sub-menu, e.g. a polyline
 * segment's "Add Vertex" / "Convert to Arc", and a 30°-snap-during-copy
 * modifier), tracked separately as the deferred `GRIPS-MULTIFUNCTIONAL-MENU`
 * follow-up. The RU/EN echo strings for each bracketed mode prompt
 * ("** STRETCH **" / "** MOVE **" / …) live in `i18n/messages.ts` under the
 * `grip.mode.*` keys; this map records the canonical AutoCAD token per mode.
 */
export const GRIP_MODE_LABELS = {
  stretch: "STRETCH",
  move: "MOVE",
  rotate: "ROTATE",
  scale: "SCALE",
  mirror: "MIRROR",
} as const;

export type GripEditMode = keyof typeof GRIP_MODE_LABELS;

/**
 * N4 GRIPS-ARMED-STRETCH-I18N — RU/EN command-line echo keys for the armed-
 * grip modal phase, keyed by `GripEditMode` (mirrors `GRIP_MODE_LABELS`'s
 * shape). `mode` is the "** STRETCH **"-style banner line — reuses PR-2's
 * `grip.mode.*` keys verbatim (the SAME text PR-2's own `useGripModeEcho`
 * status-strip echo shows, so the two surfaces never drift apart). `point`
 * is the imperative "Specify stretch point:" line beneath it, AutoCAD-
 * verified PER MODE (STRETCH/MOVE/ROTATE/SCALE/MIRROR each have a distinct
 * prompt verb — "Just Grip It!":
 * https://help.autodesk.com/cloudhelp/2021/ENU/AutoCAD-DidYouKnow/files/GUID-BBEA1F71-EB16-4D49-80D9-970A6909F508.htm).
 * Unlike AutoCAD's full "...or [Base point/Copy/Undo/eXit]:" prompt, this
 * omits the bracketed option list — those grip-edit sub-options are not
 * wired in this repo (StageGripDrag's `onKey` aborts the armed phase on any
 * keydown except the OSNAP-override exception and PR-2's own Enter/Space
 * cycle), so advertising them would be a non-functional affordance.
 */
export const GRIP_ARMED_PROMPT_KEYS: Record<
  GripEditMode,
  { readonly mode: MessageKey; readonly point: MessageKey }
> = {
  stretch: {
    mode: "grip.mode.stretch",
    point: "prompt.grip.armed.stretch.point",
  },
  move: {
    mode: "grip.mode.move",
    point: "prompt.grip.armed.move.point",
  },
  rotate: {
    mode: "grip.mode.rotate",
    point: "prompt.grip.armed.rotate.point",
  },
  scale: {
    mode: "grip.mode.scale",
    point: "prompt.grip.armed.scale.point",
  },
  mirror: {
    mode: "grip.mode.mirror",
    point: "prompt.grip.armed.mirror.point",
  },
};

/**
 * Canonical Enter/Space cycle order (AutoCAD-verified, see `GRIP_MODE_LABELS`
 * doc comment). Stretch is always the mode a fresh arm starts at.
 */
export const GRIP_EDIT_MODE_CYCLE: readonly GripEditMode[] = [
  "stretch",
  "move",
  "rotate",
  "scale",
  "mirror",
];

/**
 * Advance to the next mode in {@link GRIP_EDIT_MODE_CYCLE}, wrapping mirror
 * back to stretch. Pure — no store access, so it is trivially testable and
 * reusable by both `hotGripStore.cycleArmedEditMode` and any future caller.
 *
 * Verified-negative (no per-entity-type restriction): every entity type that
 * exposes a grip cycles through all 5 modes identically — only STRETCH's
 * per-grip RESHAPE behaviour varies by entity/grip-kind (`gripDragTranslate.ts`,
 * unchanged by this slice). Move/Rotate/Scale/Mirror apply the same whole-
 * selection rigid transform regardless of entity type (`gripModeTransform.ts`).
 */
export function nextGripEditMode(mode: GripEditMode): GripEditMode {
  const i = GRIP_EDIT_MODE_CYCLE.indexOf(mode);
  const next = GRIP_EDIT_MODE_CYCLE[(i + 1) % GRIP_EDIT_MODE_CYCLE.length];
  // `mode` is always a valid GripEditMode (indexOf never returns -1 for a
  // typed caller), so `next` is always defined — the fallback is defensive
  // only (keeps the return type `GripEditMode`, not `GripEditMode | undefined`).
  return next ?? "stretch";
}
